import './BeaconUserFlow.scss';

import { AAMBeaconButton, AAMBeaconModal } from 'components/shared/Beacon';
import React, {Fragment, useEffect, useState} from 'react';
import { func,bool } from 'prop-types';
import { connect } from 'react-redux';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';

import {
  hasAccountsForViewAccess,
  getIsRateLimitError,
  isInvalidUserInput,
  isGrantViewAccessAuthorizedAccounts,
  isGrantViewAccessAddUserRequested,
  isGrantViewAccessAddUserReceived,
  isNoEligibleAccountsError,
  getGrantViewAccessAddSaveError,
  getGrantViewAccessAddSaveSuccess
} from 'state/grantViewAccess/GrantViewAccessSelectors';
import Store from 'state/Store';
import {
  clearTouchedFields,
  touchedField
} from 'state/validation/ValidationActions';
import { getGrantViewAccessAddUserAuth } from 'services/Rest';
import BeaconNewUserForm from './NewUserForm/BeaconNewUserForm';
import BeaconSelectViewableAccounts from './SelectViewableAccounts/BeaconSelectViewableAccounts';
import { resetGrantViewAccessAddUserAuth } from 'state/grantViewAccess/GrantViewAccessActions';
import { addAccountViewAccess } from 'services/GrantViewAccess';
import {
  getPrimaryEmailAddress,
  getCustomer,
  getCustomerWebView
} from 'state/customer/CustomerSelectors';
import { getView } from 'state/view/ViewSelectors';
import { gvaNewCopyEnabled } from 'services/FeatureFlags';
import { handleGVAFormSubmit } from 'components/shared/FormValidation/GVAFormValidation';
import { GVAErrorMessage } from 'services/Content';

const stepTitles = {
  1: 'Provide access to a user',
  2: 'View-only access by investment'
};

const stepTitlesV2 = {
  1: 'Grant permission to view account(s) and use information in tools',
  2: 'Grant permission to view the following account(s) and use account information in tools'
};

const BeaconUserFlow = ({
  onClose,
  onSave,
  customer: user,
  customerEmail,
  view,
  closeCallback,
  isInvalidUserInput,
  rateLimitError,
  addUserAuthRequested,
  hasAccountsForViewAccess,
  addUserAuthReceived,
  isGrantViewAccessAddSaveSuccess,
  isGrantViewAccessAddSaveError,
  isNoEligibleAccounts,
  isCustomerWebView
}) => {
  const [currStep, setCurrStep] = useState(1);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    ssn: '',
    checkedItems: {},
    scrolledThroughDisclosure: ''
  });
  const [showSpinner, setShowSpinner] = useState(false);

  const {
    firstName: firstName,
    lastName: lastName,
    'Date of birth': birthDate,
    'Social Security number': ssn,
    checkedItems
  } = userData;

  useEffect(() => {
    if (
      isGrantViewAccessAddSaveSuccess ||
      isGrantViewAccessAddSaveError ||
      isInvalidUserInput ||
      rateLimitError ||
      isNoEligibleAccounts
    ) {
      setShowSpinner(false);
    }
    if (currStep === 1 && addUserAuthReceived) {
      setShowSpinner(false);
    }
    updateModalState();
  }, [
    isGrantViewAccessAddSaveSuccess,
    isGrantViewAccessAddSaveError,
    addUserAuthRequested,
    hasAccountsForViewAccess,
    rateLimitError,
    isInvalidUserInput,
    addUserAuthReceived,
    isNoEligibleAccounts
  ]);

  const updateModalState = () => {
    if (
      hasAccountsForViewAccess &&
      !isInvalidUserInput &&
      !rateLimitError &&
      addUserAuthReceived &&
      !isNoEligibleAccounts
    ) {
      currStep===1 && setCurrStep(currStep + 1);
     }
  };

  const checkIfShouldDisable = () => {
    if(isCustomerWebView) {
      return true;
    }
    return currStep === 2 && !Object.values(checkedItems).some((v) => v);
  };

  const updateFormFieldCheckbox = (field) => (eventOrValue) => {
    Store.dispatch(touchedField(field));
    setUserData({
      ...userData,
      [field]: eventOrValue.target ? eventOrValue.target.value : eventOrValue
    });
  };

  const updateFormField = (event) => {
    Store.dispatch(touchedField(event.target.name));
    setUserData({ ...userData, [event.target.name]: event.target.value });
  };

  const shouldDisable = checkIfShouldDisable();
  const modalTitle = gvaNewCopyEnabled() ? stepTitlesV2[currStep] : stepTitles[currStep];

  const grantee = {
    firstName,
    lastName,
    birthDate,
    taxId: ssn
  };

  const onContinue = () => {
    const requestBody = {
      firstName,
      lastName,
      birthDate,
      taxId: ssn
    };

    getGrantViewAccessAddUserAuth(requestBody);
    Store.dispatch(clearTouchedFields());
  };

  const onBack = () => {
    Store.dispatch(clearTouchedFields());
    Store.dispatch(resetGrantViewAccessAddUserAuth());
    setCurrStep(currStep - 1);
  };


  const handleSaveClick = () => {
    addAccountViewAccess(
      grantee,
      checkedItems,
      onSave,
      customerEmail,
      user,
      view
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (currStep === 1) {
      handleGVAFormSubmit(event.target,GVAErrorMessage);
      if(event.target.checkValidity()) {
        setShowSpinner(true);
        onContinue();
      }
    } else {
      setShowSpinner(true);
      handleSaveClick();
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <AAMBeaconModal
        open={true}
        scale="jumbo"
        id="gva_new_user_modal"
        onBeaconEventClose={() => closeCallback()}>
        <div className="b-typography-sans-serif--headline--200 b-font-weight-bold">{modalTitle}</div>
        {currStep === 1 ? (
          <Fragment>
            <BeaconNewUserForm
              onClose={onClose}
              onContinue={onContinue}
              firstName={firstName}
              lastName={lastName}
              birthDate={birthDate}
              shouldDisableNext={shouldDisable}
              updateFirstName={updateFormField}
              updateLastName={updateFormField}
              updateDateofBirth={updateFormField}
              updateScrollDetection={updateFormField}
              ssn={ssn}
              updateSSN={updateFormField}
              isInvalidUserInput={isInvalidUserInput}
              isNoEligibleAccounts={isNoEligibleAccounts}
            />
          </Fragment>
        ) : (
          <Fragment>
            <BeaconSelectViewableAccounts
              checkedItems={checkedItems}
              onClose={onClose}
              grantee={grantee}
              onBack={onBack}
              onSave={onSave}
              shouldDisableSave={shouldDisable}
              updateCheckedItems={updateFormFieldCheckbox('checkedItems')}
            />
          </Fragment>
        )}
        <div className="new-user-modal-button-group">
          <div slot="action-zone" className="new-user-modal-button-group-next">
            <AAMBeaconButton
              variant={currStep === 1 ? 'tertiary' : 'secondary'}
              scale="jumbo"
              onBeaconEventClick={() => {
                if (currStep === 1) {
                  onClose();
                } else {
                  onBack();
                }
              }}
            >
                {currStep === 1 ? 'Cancel' : 'Back'}
            </AAMBeaconButton>
          </div>
          {showSpinner && (
            <div className="loading-spinner">
              <Spinner />
            </div>
          )}
          {!showSpinner && (
            <AAMBeaconButton
              variant="primary"
              label={currStep === 1 ? 'Next' : 'Save'}
              scale="jumbo"
              type="submit"
              disabled={shouldDisable}
            >
                {currStep === 1 ? 'Next' : 'Save'}
            </AAMBeaconButton>
          )}
        </div>
      </AAMBeaconModal>
    </form>
  );
};

BeaconUserFlow.propTypes = {
  onClose: func.required,
  onSave: func.required,
  isInvalidUserInput: bool,
  rateLimitError: bool,
  addUserAuthRequested: bool,
  hasAccountsForViewAccess: bool,
  addUserAuthReceived: bool,
  isNoEligibleAccounts: bool
};

const mapStateToProps = (state) => ({
  isInvalidUserInput: isInvalidUserInput(state),
  rateLimitError: getIsRateLimitError(state),
  hasAccountsForViewAccess: hasAccountsForViewAccess(state),
  addUserAuthorized: isGrantViewAccessAuthorizedAccounts(state),
  addUserAuthRequested: isGrantViewAccessAddUserRequested(state),
  addUserAuthReceived: isGrantViewAccessAddUserReceived(state),
  customer: getCustomer(state),
  customerEmail: getPrimaryEmailAddress(state).address || '',
  view: getView(state),
  isGrantViewAccessAddSaveSuccess: getGrantViewAccessAddSaveSuccess(state),
  isGrantViewAccessAddSaveError: getGrantViewAccessAddSaveError(state),
  isNoEligibleAccounts: isNoEligibleAccountsError(state),
  isCustomerWebView: getCustomerWebView(state)
});

export default connect(mapStateToProps)(BeaconUserFlow);
